import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useUsersQuery } from 'hooks/queries/useUsersQuery';
import { getUserDisplayLabel } from 'utilities/fullName';
import { Flex, ImageInput } from 'components';
import { IGenericModal, Modal } from 'components/Modal/Modal';
import { NestedSelect } from 'components/_form/NestedSelect/NestedSelect';
import { flattenOptions } from 'components/_form/NestedSelect/utilities';
import { TextField, UserSelect } from 'components/_form';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ITeamForm } from 'types/forms/team';
import { ITeam } from 'types/team';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import { getFile } from 'utilities/files';
import { getTeamMembers } from 'utilities/teamMembers';
import { toUserSelectOption } from 'utilities/userSelectOption';

const Row = styled(Flex)`
  width: 100%;
  align-items: center;
  gap: 10px;
`;

const ImageBox = styled(Flex)`
  width: 110px;
  height: 110px;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-left: 5px;

  ${({ theme }) =>
    `color: ${theme.palette.text.secondary};
  border: 1px dashed ${theme.palette.neutral.medium};`};
`;

interface Props extends IGenericModal {
  onSubmit: (data: ITeamForm['team']) => Promise<void>;
  selectedItemData?: ITeam;
}

export const TeamAddModal = ({
  isOpen,
  onCancelClick,
  onSubmit,
  selectedItemData
}: Props) => {
  const { data: usersData = [] } = useUsersQuery();
  const users = useMemo(
    () =>
      usersData.map((user) =>
        toUserSelectOption({
          id: user.id,
          email: user.email,
          username: user.username,
          profile: user.profile
        })
      ),
    [usersData]
  );
  const { data: areasData = [] } = useAreasQuery();
  const areas = useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );
  const { t } = useTranslation();

  const { register, handleSubmit, reset, setValue, watch, control } = useForm();

  const loadData = async () => {
    if (selectedItemData) {
      const {
        id,
        state,
        image_urls,
        logo_url,
        leader,
        manager,
        areas,
        members_teams: _membersTeams,
        ...restData
      } = selectedItemData;

      reset({
        ...restData,
        images: image_urls.length
          ? (await Promise.all(
              image_urls.map((image_url) => getFile(image_url))
            ).then((values) => {
              return values;
            })) || []
          : [],
        logo: logo_url ? await getFile(logo_url) : undefined,
        leader: {
          value: leader.id,
          label: getUserDisplayLabel({
            first_name: (leader as any).first_name,
            last_name: (leader as any).last_name,
            email: leader.email,
            username: leader.username
          })
        },
        manager: {
          value: manager.id,
          label: getUserDisplayLabel({
            first_name: (manager as any).first_name,
            last_name: (manager as any).last_name,
            email: manager.email,
            username: manager.username
          })
        },
        areas: areas.map((area) => ({
          value: String(area.id),
          label: area.materialized_path_label ?? area.name
        })),
        members: getTeamMembers(selectedItemData).map((member) => ({
          value: member.id,
          label: getUserDisplayLabel({
            first_name: member.first_name,
            last_name: member.last_name,
            email: (member as any).email,
            username: member.username
          })
        }))
      });
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedItemData]);

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={() => {
        reset();
        onCancelClick();
      }}
      mainButton={{
        label: t('buttons.add'),
        action: () => {
          handleSubmit(onSubmit)();
          reset();
        }
      }}
      headerTitle={t('employeesAndUsersView.addTeam')}
      isSecondButtonVisible
    >
      <form>
        <Flex flexDirection="column">
          <Row flexWrap={['wrap', 'nowrap']} alignItems="flex-end">
            <TextField
              {...register('id_code')}
              label={t('employeesAndUsersView.form.id')}
            />
            <TextField
              {...register('name')}
              label={t('employeesAndUsersView.form.teamName')}
            />
            <ImageInput
              defaultImage={
                selectedItemData?.image_urls?.length
                  ? `${process.env.REACT_APP_API_URL}${selectedItemData?.image_urls[0]}`
                  : ''
              }
              setValue={(file: Blob | undefined) => {
                setValue('images', file ? [file] : []);
              }}
              type="photo"
              placeholder={t('employeesAndUsersView.form.photoPlaceholder')}
            />
          </Row>
          <Row flexWrap={['wrap', 'nowrap']} alignItems="flex-end">
            <Flex flexDirection="column" width="100%">
              <Row flexWrap={['wrap', 'nowrap']} alignItems="flex-end">
                <Controller
                  control={control}
                  name="leader"
                  render={({ field: { onChange, value } }) => (
                    <UserSelect
                      label={t('employeesAndUsersView.form.teamLeader')}
                      selectedOptions={value}
                      onChange={(val) => {
                        onChange(val);
                      }}
                      options={users}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="manager"
                  render={({ field: { onChange, value } }) => (
                    <UserSelect
                      label={t('employeesAndUsersView.form.teamManager')}
                      selectedOptions={value}
                      onChange={(val) => {
                        onChange(val);
                      }}
                      options={users}
                    />
                  )}
                />
              </Row>
              <Row flexWrap={['wrap', 'nowrap']} alignItems="flex-end">
                <Controller
                  control={control}
                  name="areas"
                  render={({ field: { onChange, value } }) => (
                    <NestedSelect
                      label={t('employeesAndUsersView.form.areas') as string}
                      selectedOptions={flattenOptions(areas).filter((option) =>
                        value?.some(
                          (selected: { value: string }) =>
                            String(selected.value) === String(option.value)
                        )
                      )}
                      setSelectedOptions={(newOptions) => {
                        onChange(
                          flattenOptions(newOptions).map((option) => ({
                            value: String(option.value),
                            label: option.label
                          }))
                        );
                      }}
                      options={areas}
                    />
                  )}
                />
              </Row>
            </Flex>
            <Flex width="50%">
              <ImageInput
                defaultImage={
                  selectedItemData?.logo_url &&
                  `${process.env.REACT_APP_API_URL}${selectedItemData?.logo_url}`
                }
                setValue={(file: Blob | undefined) => {
                  setValue('logo', file);
                }}
                type="logo"
                placeholder={t('employeesAndUsersView.form.photoPlaceholder')}
              />
            </Flex>
          </Row>
        </Flex>
      </form>
    </Modal>
  );
};
